
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Facebook, Twitter, Instagram, Save, X, Youtube, Share2 } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { emotionEmojiMap, getEmojiForEmotion } from '../EmotionDetector';
import { toast } from 'sonner';
import SocialSharePanel from '../SocialSharePanel';

interface EmotionSelectorProps {
  selectedEmotion: string | null;
  onSelectEmotion: (emotion: string, emojiUrl: string) => void;
}

const EmotionSelector: React.FC<EmotionSelectorProps> = ({ selectedEmotion, onSelectEmotion }) => {
  const emotions = [
    { name: 'happy', label: 'Happy' },
    { name: 'ecstatic', label: 'Ecstatic' },
    { name: 'ohmygod', label: 'Oh My God' },
    { name: 'just-ok', label: 'Just OK' },
    { name: 'neutral', label: 'Neutral' },
    { name: 'sad', label: 'Sad' },
    { name: 'surprised', label: 'Surprised' },
    { name: 'fear', label: 'Fear' },
    { name: 'angry', label: 'Angry' },
    { name: 'bored', label: 'Bored' },
    { name: 'contempt', label: 'Contempt' },
    { name: 'startled', label: 'Startled' },
    { name: 'anxious', label: 'Anxious' },
    { name: 'flat', label: 'Flat' }, 
    { name: 'disgust', label: 'Disgust' }
  ];

  return (
    <div className="grid grid-cols-5 gap-2">
      {emotions.map(emotion => {
        const emojiSrc = emotionEmojiMap[emotion.name] || emotionEmojiMap.default;
        const isSelected = selectedEmotion === emotion.name;
        
        return (
          <div 
            key={emotion.name}
            onClick={() => onSelectEmotion(emotion.name, emojiSrc)}
            className={`
              flex flex-col items-center p-2 rounded cursor-pointer transition-all
              ${isSelected ? 'bg-yellow-500/20 ring-2 ring-yellow-500' : 'hover:bg-gray-800'}
            `}
          >
            <img 
              src={emojiSrc} 
              alt={emotion.label}
              className="w-8 h-8 mb-1"
            />
            <span className="text-xs text-center">{emotion.label}</span>
          </div>
        );
      })}
    </div>
  );
};

interface SocialShareProps {
  onShareToSocial: (platform: string) => void;
  onShareToAll: () => void;
  sharedPlatforms: string[];
  videoUrl: string;
  title: string;
  description: string;
  userId?: string;
  merchantId?: string | null;
}

const SocialShare: React.FC<SocialShareProps> = ({ 
  onShareToSocial, 
  onShareToAll, 
  sharedPlatforms,
  videoUrl,
  title,
  description,
  userId,
  merchantId
}) => {
  const [isSharing, setIsSharing] = useState(false);

  const handleShareAll = async () => {
    setIsSharing(true);
    await onShareToAll();
    setIsSharing(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-4">
        <Button 
          className="w-full bg-gradient-to-r from-blue-600 via-pink-500 to-red-600 hover:opacity-90 text-white font-bold"
          onClick={handleShareAll}
          disabled={isSharing}
        >
          {isSharing ? (
            <>
              <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
              Sharing...
            </>
          ) : (
            <>
              <Share2 className="mr-2 h-5 w-5" /> Share to All Platforms
            </>
          )}
        </Button>
        
        <p className="text-xs text-center text-gray-400 mt-1">Or share individually:</p>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        <Button 
          variant="outline" 
          className="bg-blue-600 text-white hover:bg-blue-700"
          onClick={() => onShareToSocial('Facebook')}
          disabled={sharedPlatforms.includes('Facebook')}
        >
          <Facebook className="mr-2 h-4 w-4" /> 
          {sharedPlatforms.includes('Facebook') ? 'Shared' : 'Facebook'}
        </Button>
        <Button 
          variant="outline" 
          className="bg-sky-500 text-white hover:bg-sky-600"
          onClick={() => onShareToSocial('Twitter')}
          disabled={sharedPlatforms.includes('Twitter')}
        >
          <Twitter className="mr-2 h-4 w-4" /> 
          {sharedPlatforms.includes('Twitter') ? 'Shared' : 'Twitter'}
        </Button>
        <Button 
          variant="outline" 
          className="bg-pink-600 text-white hover:bg-pink-700"
          onClick={() => onShareToSocial('Instagram')}
          disabled={sharedPlatforms.includes('Instagram')}
        >
          <Instagram className="mr-2 h-4 w-4" /> 
          {sharedPlatforms.includes('Instagram') ? 'Shared' : 'Instagram'}
        </Button>
        <Button 
          variant="outline" 
          className="bg-red-600 text-white hover:bg-red-700"
          onClick={() => onShareToSocial('YouTube')}
          disabled={sharedPlatforms.includes('YouTube')}
        >
          <Youtube className="mr-2 h-4 w-4" /> 
          {sharedPlatforms.includes('YouTube') ? 'Shared' : 'YouTube'}
        </Button>
      </div>
      
      {/* Direct integration with SocialSharePanel for better sharing functionality */}
      <div className="mt-4">
        <SocialSharePanel
          videoId={0}  // This will be overridden by videoUrl
          videoUrl={videoUrl}
          title={title}
          description={description}
          userId={userId}
          merchantId={merchantId}
        />
      </div>
    </div>
  );
};

interface RecordedVideoReviewProps {
  recordedVideo: string;
  dominantEmotion: string | null;
  dominantEmoji: string | null;
  dominantEmotionPercentage: string | null;
  selectedEmotion: string | null;
  selectedEmoji: string | null;
  title: string;
  description: string;
  setTitle: (title: string) => void;
  setDescription: (description: string) => void;
  onSelectEmotion: (emotion: string, emojiUrl: string) => void;
  onShareToSocial: (platform: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  merchantId?: string | null;
}

const RecordedVideoReview: React.FC<RecordedVideoReviewProps> = ({
  recordedVideo,
  dominantEmotion,
  dominantEmoji,
  dominantEmotionPercentage,
  selectedEmotion,
  selectedEmoji,
  title,
  description,
  setTitle,
  setDescription,
  onSelectEmotion,
  onShareToSocial,
  onSave,
  onCancel,
  isSaving,
  merchantId
}) => {
  const [sharedPlatforms, setSharedPlatforms] = useState<string[]>([]);
  const [videoSaved, setVideoSaved] = useState<boolean>(false);
  
  // Format the dominant emotion to display properly
  const displayEmotion = (() => {
    const emotion = selectedEmotion || dominantEmotion;
    
    // Check if it's an object and handle appropriately (fixes [object Object] issue)
    if (emotion && typeof emotion === 'object') {
      console.warn('Emotion is an object:', emotion);
      return 'neutral'; // Default fallback
    }
    
    return emotion || 'neutral';
  })();
  
  // Get the correct emoji URL
  const displayEmoji = (() => {
    // Use selected emoji if available
    if (selectedEmoji && typeof selectedEmoji === 'string') {
      return selectedEmoji;
    }
    
    // Use dominant emoji if available and it's a string
    if (dominantEmoji && typeof dominantEmoji === 'string') {
      return dominantEmoji;
    }
    
    // Otherwise use the emoji for the current emotion
    return getEmojiForEmotion(displayEmotion);
  })();

  // Handle individual platform sharing
  const handleShareToSocial = async (platform: string) => {
    try {
      await onShareToSocial(platform);
      setSharedPlatforms(prev => [...prev, platform]);
      toast.success(`Shared to ${platform}!`);
    } catch (error) {
      console.error(`Error sharing to ${platform}:`, error);
      toast.error(`Failed to share to ${platform}`);
    }
  };

  // Handle sharing to all platforms
  const handleShareToAll = async () => {
    const platforms = ['Facebook', 'Twitter', 'Instagram', 'YouTube'];
    let successCount = 0;
    
    for (const platform of platforms) {
      if (!sharedPlatforms.includes(platform)) {
        try {
          await onShareToSocial(platform);
          setSharedPlatforms(prev => [...prev, platform]);
          successCount++;
        } catch (error) {
          console.error(`Error sharing to ${platform}:`, error);
        }
      }
    }
    
    if (successCount > 0) {
      toast.success(`Shared to ${successCount} platform${successCount > 1 ? 's' : ''}!`);
    } else {
      toast.error("Failed to share to any platforms");
    }
    
    return successCount > 0;
  };

  // Custom save handler that enables sharing after saving
  const handleSave = async () => {
    await onSave();
    setVideoSaved(true);
    toast.success("Video saved successfully! Now you can share it.");
  };

  return (
    <div className="absolute inset-0 z-30 bg-black/80 p-4 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl text-white font-semibold">Review Your Recording</h2>
          <Button 
            variant="ghost" 
            className="text-white hover:bg-gray-800"
            onClick={onCancel}
          >
            <X />
          </Button>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Video Preview */}
          <div className="lg:col-span-2">
            <div className="bg-black rounded-lg overflow-hidden">
              <video 
                src={recordedVideo} 
                controls 
                autoPlay
                className="w-full h-auto"
              ></video>
            </div>
          </div>
          
          {/* Video Details */}
          <div className="lg:col-span-1">
            <Card className="bg-gray-900 border-gray-800 text-white">
              <CardContent className="p-4">
                <Tabs defaultValue={videoSaved ? "share" : "details"}>
                  <TabsList className="bg-gray-800 mb-4">
                    <TabsTrigger value="details" className="data-[state=active]:bg-gray-700">Details</TabsTrigger>
                    <TabsTrigger value="emotions" className="data-[state=active]:bg-gray-700">Emotions</TabsTrigger>
                    <TabsTrigger value="share" className="data-[state=active]:bg-gray-700">Share</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="details">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Title
                        </label>
                        <Input
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white"
                          placeholder="Give your video a title"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Description
                        </label>
                        <Textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          className="bg-gray-800 border-gray-700 text-white h-24"
                          placeholder="Add a description..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Dominant Emotion
                        </label>
                        <div className="flex items-center bg-gray-800 p-3 rounded">
                          {displayEmoji && (
                            <img 
                              src={displayEmoji} 
                              className="w-6 h-6 mr-2"
                              alt={displayEmotion}
                            />
                          )}
                          <span className="capitalize">{displayEmotion}</span>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="emotions">
                    <p className="text-sm text-gray-400 mb-3">
                      Select the emotion that best represents your video:
                    </p>
                    <EmotionSelector
                      selectedEmotion={selectedEmotion}
                      onSelectEmotion={onSelectEmotion}
                    />
                  </TabsContent>
                  
                  <TabsContent value="share">
                    <p className="text-sm text-gray-400 mb-3">
                      Share your video to social media:
                    </p>
                    <SocialShare 
                      onShareToSocial={handleShareToSocial} 
                      onShareToAll={handleShareToAll}
                      sharedPlatforms={sharedPlatforms}
                      videoUrl={recordedVideo}
                      title={title || "Check out my video!"}
                      description={description || `I'm feeling ${displayEmotion}!`}
                      userId={undefined} // Will use the current user
                      merchantId={merchantId}
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="flex justify-center mt-6 space-x-4">
          <Button 
            variant="outline" 
            className="border-white text-white hover:bg-gray-800"
            onClick={onCancel}
          >
            <X className="mr-2" /> Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={isSaving}
            className="bg-yellow-600 hover:bg-yellow-700"
          >
            {isSaving ? (
              <>
                <div className="animate-spin mr-2 h-4 w-4 border-t-2 border-b-2 border-white rounded-full"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2" /> Save Video
              </>
            )}
          </Button>
        </div>

        {/* Show share buttons prominently after saving */}
        {videoSaved && (
          <div className="mt-8 bg-gray-900 border border-gray-700 p-4 rounded-lg">
            <h3 className="text-xl text-center text-white mb-4">Your video has been saved! Share it now:</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Button 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => handleShareToSocial('Facebook')}
              >
                <Facebook className="mr-2 h-5 w-5" /> Facebook
              </Button>
              <Button 
                className="bg-blue-400 hover:bg-blue-500 text-white"
                onClick={() => handleShareToSocial('Twitter')}
              >
                <Twitter className="mr-2 h-5 w-5" /> Twitter
              </Button>
              <Button 
                className="bg-pink-600 hover:bg-pink-700 text-white"
                onClick={() => handleShareToSocial('Instagram')}
              >
                <Instagram className="mr-2 h-5 w-5" /> Instagram
              </Button>
              <Button 
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleShareToSocial('YouTube')}
              >
                <Youtube className="mr-2 h-5 w-5" /> YouTube
              </Button>
            </div>
            <Button 
              className="w-full mt-3 bg-gradient-to-r from-blue-600 via-pink-500 to-red-600 hover:opacity-90 text-white font-bold"
              onClick={handleShareToAll}
            >
              <Share2 className="mr-2 h-5 w-5" /> Share to All Platforms
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordedVideoReview;
